import React, { useEffect, useState } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { Box, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { Close as CloseIcon, Description } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import ListTable from '../../../components/table/listTable';
import { useGetAllDataMutation } from '../../../components/services/services';
import { formatMoney } from '../../../utils/currencyConvert';

type Props = {
  open?: boolean;
  onClose?: () => void;
  companyId?: number;
  processId?: number;
};

const moneyFormatter = new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  minimumFractionDigits: 2,
});

function formatTry(value: any) {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(num)) return '';
  return moneyFormatter.format(num);
}

const columns: GridColDef[] = [
  {
    // string alan üzerinden filtrelenecek
    field: 'balanceBeforeProcessText',
    headerName: 'İşlem Öncesi Bakiye',
    width: 200,
    renderCell: (params: GridRenderCellParams) => formatMoney(params, 'balanceBeforeProcess'),
  },
  {
    // string alan üzerinden filtrelenecek
    field: 'balanceAfterProcessText',
    headerName: 'İşlem Sonrası Bakiye',
    width: 200,
    renderCell: (params: GridRenderCellParams) => formatMoney(params, 'balanceAfterProcess'),
  },
  {
    field: 'description',
    headerName: 'Bakiye Türü',
    width: 240,
  },
];

export default function BalanceDetailClientTable({ open = false, onClose, companyId, processId }: Props) {
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>({});
  const [rows, setRows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [getAllData] = useGetAllDataMutation();

  // listTable'ın prop typing'i dar; bu yüzden any cast ile kullanıyoruz.
  const ListTableAny = ListTable as any;

  useEffect(() => {
    if (!open) return;
    if (!companyId || !processId) {
      setRows([]);
      return;
    }

    setIsLoading(true);
    setRows([]);

    getAllData({
      url: `/Process/getCustomerBalanceDetailAfterProcessList?companyId=${companyId}&processId=${processId}`,
      filter: {},
      method: 'POST',
    })
      .unwrap()
      .then((result: any) => {
        // Response stabil: { totalCount, ..., data: [...] }
        const arr = Array.isArray(result?.data) ? result.data : [];
        const mapped = arr
          // İki alan da null ise satırı göstermeyelim
          .filter((item: any) => item?.balanceBeforeProcess != null || item?.balanceAfterProcess != null)
          .map((item: any, idx: number) => ({
            ...item,
            id: item?.id ?? idx,
            balanceBeforeProcessText: formatTry(item?.balanceBeforeProcess),
            balanceAfterProcessText: formatTry(item?.balanceAfterProcess),
          }));
        setRows(mapped);
      })
      .catch(() => {
        enqueueSnackbar('Bakiye detayı alınırken hata oluştu.', { variant: 'error' });
      })
      .finally(() => setIsLoading(false));
  }, [open, companyId, processId, getAllData]);

  const table = (
    <ListTableAny
      columns={columns}
      filterKey="TransactionBalanceDetailClientTableFilter"
      columnVisibilityConfigKey="TransactionBalanceDetailClientTableColumnVisibilityKey"
      hasActiveFilters={false}
      filterMode="client"
      headerFilters={true}
      autoHeight={false}
      rows={rows}
      isLoading={isLoading}
      getRowId={(row: any) => row.id}
      checkboxSelection={true}
      tableHeaderName=""
      exportFileName={processId ? `Bakiye Detayı_${processId}` : 'Bakiye Detayı'}
      // İndir butonu aktif olsun
      disabledDownload={false}
      columnVisibilityModel={columnVisibilityModel}
      setColumnVisibilityModel={setColumnVisibilityModel}
      sx={{
        height: '100%',
        '& .MuiDataGrid-root': {
          borderRadius: 2,
          border: 'none',
        },
        '& .MuiDataGrid-main': {
          borderRadius: 2,
        },
        '& .MuiDataGrid-columnHeaders': {
          background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.9) 0%, rgba(241, 245, 249, 0.95) 100%)',
          borderBottom: '2px solid rgba(226, 232, 240, 0.8)',
          borderRadius: '8px 8px 0 0',
        },
      }}
    />
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: (theme: any) => ({
          width: { xs: '95vw', sm: '90vw', md: '90vw' },
          height: { xs: '90vh', sm: '85vh', md: '80vh' },
          maxWidth: 'none',
          m: { xs: 1, sm: 2, md: 3 },
          backgroundColor: theme.palette.grey[50],
          overflow: 'hidden',
        }),
      }}
    >
      <DialogTitle component="div" sx={{ p: 0, m: 0 }}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
            padding: { xs: '12px 16px', sm: '16px 20px' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              zIndex: 0,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Description sx={{ fontSize: 18 }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                color: 'white',
                fontWeight: 700,
                fontSize: { xs: '0.95rem !important', sm: '1rem !important' },
                textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                lineHeight: 1.3,
              }}
            >
              Bakiye Detayı
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              position: 'relative',
              zIndex: 1,
              flexShrink: 0,
              width: 36,
              height: 36,
              border: '1px solid',
              borderColor: 'rgba(255,255,255,0.3)',
              backgroundColor: 'rgba(255,255,255,0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderColor: 'rgba(255,255,255,0.5)',
                transform: 'rotate(90deg)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          px: { xs: 2, sm: 3 },
          py: 0,
          pb: { xs: 2, sm: 3 },
          overflow: 'hidden',
        }}
      >
        <Box sx={{ pt: { xs: 2.5, sm: 3 }, width: '100%' }}>
          <Box
            sx={{
              height: { xs: 'calc(90vh - 140px)', sm: 'calc(85vh - 140px)', md: '60vh' },
              width: '100%',
            }}
          >
          {isLoading ? (
            <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            table
          )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

